/*global chrome*/
import { Amplify } from "aws-amplify";
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  completeNewPassword,
  changePassword,
  signInWithRedirect,
} from "aws-amplify/auth";
import { isTestEnv, objectToQueryString } from "../util/Utils";
import { logDebug, logError, logInfo } from "./ServiceUtil";
import Router from "next/router";

export const oauthOrigin =
  typeof window !== "undefined" &&
  `https://www${window.location.hostname.substring(
    window.location.hostname.indexOf("."),
    window.location.hostname.length,
  )}`;

export const configureAmplify = (useToken = false) => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: isTestEnv ? "us-west-2_JpbAzdptt" : "us-west-2_Bekkz1let",
        userPoolClientId: isTestEnv
          ? "7i5ut48idomd83nu4q9t4usvgl"
          : "77imq6jtqtvmpocnjjcl5ht1mu",
        loginWith: {
          oauth: {
            domain: isTestEnv
              ? "auth-dev.pixelmixer.com"
              : "auth.pixelmixer.com",
            scopes: ["email", "openid"],
            redirectSignIn: [
              `${oauthOrigin}/oauthLogin${useToken ? "/token" : ""}`,
            ],
            redirectSignOut: [`${oauthOrigin}/oauthSignOut`],
            responseType: useToken ? "token" : "code",
          },
        },
      },
    },
  });
};

let signedInUser = undefined;
if (typeof window !== "undefined") window.LOG_LEVEL = "DEBUG";

export const signinCognito = async (username, password) => {
  try {
    signedInUser = await signIn({ username: username.toLowerCase(), password });
    logDebug("signedInUser.challengeName: " + signedInUser.challengeName);

    if (
      signedInUser.challengeName === "SMS_MFA" ||
      signedInUser.challengeName === "SOFTWARE_TOKEN_MFA"
    ) {
      // You need to get the code from the UI inputs
      // and then trigger the following function with a button click
      //    const code = getCodeFromUserInput();
      // If MFA is enabled, sign-in should be confirmed with the confirmation code
      // const loggedUser = await Auth.confirmSignIn(
      //     user,   // Return object from Auth.signIn()
      //     code,   // Confirmation code
      //     mfaType // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
      // );
    } else if (signedInUser.challengeName === "NEW_PASSWORD_REQUIRED") {
      const { requiredAttributes } = signedInUser.challengeParam; // the array of required attributes, e.g ['email', 'phone_number']
      // You need to get the new password and required attributes from the UI inputs
      // and then trigger the following function with a button click
      // For example, the email and phone_number are required attributes
      // const { username, email, phone_number } = getInfoFromUserInput();
      // const loggedUser = await Auth.completeNewPassword(
      //     user,               // the Cognito User Object
      //     newPassword,       // the new password
      //     // OPTIONAL, the required attributes
      //     {
      //         email,
      //         phone_number,
      //     }
      // );
    } else if (signedInUser.challengeName === "MFA_SETUP") {
      // This happens when the MFA method is TOTP
      // The user needs to setup the TOTP before using it
      // More info please check the Enabling MFA part
      Auth.setupTOTP(signedInUser);
    } else {
      // The signedInUser directly signs in
      const cred = await fetchAuthSession();
    }
    return signedInUser;
  } catch (cognitorError) {
    logDebug(cognitorError);

    // For aws-amplify v6.15.5, exceptions are structured differently
    // Extract error code from the error name or underlyingError
    let errorCode = cognitorError.name;
    if (cognitorError.underlyingError?.name) {
      errorCode = cognitorError.underlyingError.name;
    }

    const error = {
      userMessage: cognitorError.message,
      errorCode: errorCode,
    };

    if (errorCode === "UserNotConfirmedException") {
      // The error happens if the user didn't finish the confirmation step when signing up
      // In this case you need to resend the code and confirm the user
      // About how to resend the code and confirm the user, please check the signUp part
    } else if (errorCode === "PasswordResetRequiredException") {
      // The error happens when the password is reset in the Cognito console
      // In this case you need to call forgotPassword to reset the password
      // Please check the Forgot Password part.
    } else if (errorCode === "NotAuthorizedException") {
      // The error happens when the incorrect password is provided
      if (
        cognitorError.message.toLowerCase().indexOf("temporary password") >= 0
      )
        error.errorCode = "PasswordResetRequiredException";
    } else if (errorCode === "UserNotFoundException") {
      // The error happens when the supplied username/email does not exist in the Cognito user pool
    } else if (errorCode === "UserAlreadyAuthenticatedException") {
      // The error happens when Amplify still has an active local auth session.
      error.userMessage =
        "There is already a signed in user. Please sign out before signing in again.";
      Router.push("/oauthSignOut");
    }
    throw error;
  }
};

export const signoutCognito = async () => {
  try {
    await signOut({
      global: false,
      oauth: {
        redirectUrl: `${oauthOrigin}/oauthSignOut`,
      },
    });
  } catch (error) {
    logError("signoutCognito", {}, error);
  }
};

export const signinOauth = async (provider, origin, pathname, search, hash) => {
  const redirectPayload = {
    provider: { custom: provider }, //ie. CognitoHostedUIIdentityProvider.Google
    customState: JSON.stringify({ origin, pathname, search, hash }),
  };
  try {
    return await signInWithRedirect(redirectPayload);
  } catch (e) {
    //This issue was causing Priceline some issues so monitoring for occurrences:
    logError("signinOauth", redirectPayload, e);
    let errorCode = e.name;
    if (e.underlyingError?.name) {
      errorCode = e.underlyingError.name;
    }
    if (errorCode === "UserAlreadyAuthenticatedException") {
      logInfo("Pushing to oauthSignOut");
      Router.push(`/oauthSignOut?redirectUrl=${encodeURIComponent(origin)}`);
    }

    throw e;
  }
};

/**
 * This method is a workaround because Google oAuth Sign in is for some reason
 * not working when Amplify is configured with responseType: "token" AND
 * invoking signinOauth("Google") from PixelMixerApp (from site-app works fine)
 */
export const redirectToGoogleSignIn = async () => {
  // Make sure the user is fully signed out before redirecting them to sign back in:
  // This avoids a scenario where the user is signed in to app and site with diff creds
  // particularly from GoogleSignInRequired scenarios
  try {
    await signoutCognito();
  } catch (e) {
    logError("redirectToGoogleSignIn", {}, e);
  }
  if (window) window.location = `${oauthOrigin}${generateGoogleSignInPath()}`;
};

export const generateGoogleSignInPath = (redirectPathname) => {
  const { origin, search, pathname, hash } = window.location;
  //Use either the redirectPathname or search/hash/pathname from location:
  return `/oauthSignIn${objectToQueryString({
    provider: "Google",
    origin,
    search: redirectPathname ? "" : search,
    pathname: redirectPathname || pathname,
    hash: redirectPathname ? "" : hash,
  })}`;
};

export const generateLoginPath = (redirectPathname) => {
  return `/login${
    redirectPathname && redirectPathname !== "/"
      ? "?redirect=" + encodeURIComponent(redirectPathname)
      : ""
  }`;
};

export const generateSSOLoginPath = (redirectPathname) => {
  return `/ssoLogin${
    redirectPathname && redirectPathname !== "/"
      ? "?redirect=" + encodeURIComponent(redirectPathname)
      : ""
  }`;
};

export const generateCreateAccountPath = (redirectPathname) => {
  return `/createAccount${
    redirectPathname && redirectPathname !== "/"
      ? "?redirect=" + encodeURIComponent(redirectPathname)
      : ""
  }`;
};

export const getCognitoUser = async () => {
  try {
    return await getCurrentUser();
  } catch (error) {
    return null;
  }
};

export const getCognitoCredentials = async () => {
  try {
    if (await getCognitoUser()) return await fetchAuthSession();
  } catch (e) {
    logDebug(e);
  }
};

export const getCognitoSubject = async () => {
  const cred = await getCognitoCredentials();
  return cred?.tokens?.accessToken?.payload?.sub || null;
};

export const getCognitoIdToken = async () => {
  const cred = await getCognitoCredentials();
  return cred?.tokens?.idToken?.toString() || null;
};

export const getCognitoAccessToken = async () => {
  const cred = await getCognitoCredentials();
  return cred?.tokens?.accessToken?.toString() || null;
};

export const getAuthorizationHeaderValue = async () => {
  const token = await getCognitoAccessToken();
  return token ? "Bearer " + token : null;
};

export const getIdentityHeaderValue = async () => {
  const token = await getCognitoIdToken();
  return token ? "Bearer " + token : null;
};

export const rememberDevice = async (value) => {
  const user = await getCognitoUser();
  if (user)
    value
      ? user.setDeviceStatusRemembered()
      : user.setDeviceStatusNotRemembered();
};

export const getDeviceKey = async () => {
  const user = await getCognitoUser();
  return user ? user.deviceKey() : null;
};

export const changeCognitoPassword = async (currentPassword, newPassword) => {
  const user = await getCognitoUser();
  logDebug("signedInUser", signedInUser);
  return await changePassword({ oldPassword: currentPassword, newPassword })
    .then((user) => {
      logDebug("complete new password", user);
      if (user.challengeName === "SMS_MFA") {
        logDebug("confirmSignIn", user);
      } else if (user.challengeName === "MFA_SETUP") {
        logDebug("TOTP setup", user.challengeParam);
      } else {
        logDebug(user);
      }
      return user;
    })
    .catch((cognitoError) => {
      let { message } = cognitoError;

      // For aws-amplify v6.15.5, extract error code from the error name or underlyingError
      let errorCode = cognitoError.name;
      if (cognitoError.underlyingError?.name) {
        errorCode = cognitoError.underlyingError.name;
      }

      if (message.indexOf(":") > 0)
        message = message.substring(message.indexOf(":"));
      const error = {
        userMessage: message,
        errorCode: errorCode,
      };
      throw error;
    });
};

export const completeCognitoPasswordReset = async (password) => {
  // const { given_name, name, family_name, email } = signedInUser.attributes;
  logDebug("signedInUser", signedInUser);
  return await completeNewPassword({ challengeResponse: password })
    .then((user) => {
      logDebug("complete new password", user);
      if (user.challengeName === "SMS_MFA") {
        logDebug("confirmSignIn", user);
      } else if (user.challengeName === "MFA_SETUP") {
        logDebug("TOTP setup", user.challengeParam);
      } else {
        logDebug(user);
      }
      return user;
    })
    .catch((cognitoError) => {
      let { message } = cognitoError;

      // For aws-amplify v6.15.5, extract error code from the error name or underlyingError
      let errorCode = cognitoError.name;
      if (cognitoError.underlyingError?.name) {
        errorCode = cognitoError.underlyingError.name;
      }

      if (message.indexOf(":") > 0)
        message = message.substring(message.indexOf(":"));
      const error = {
        userMessage: message,
        errorCode: errorCode,
      };
      throw error;
    });
};

export const refreshToken = (token) => {
  fetch("https://cognito-idp.us-west-2.amazonaws.com/", {
    headers: {
      "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      "Content-Type": "application/x-amz-json-1.1",
    },
    mode: "cors",
    cache: "no-cache",
    method: "POST",
    body: JSON.stringify({
      ClientId: "77imq6jtqtvmpocnjjcl5ht1mu",
      AuthFlow: "REFRESH_TOKEN_AUTH",
      AuthParameters: {
        REFRESH_TOKEN: token,
        //SECRET_HASH: "your_secret", // In case you have configured client secret
      },
    }),
  }).then((res) => {
    return res.json(); // this will give jwt id and access tokens
  });
};
